/**
 * Utility functions for parsing dynamic variables from different data formats
 */

import { decodeVarsPayload } from './base64url';

const normalizeKeys = (obj) => {
  const out = {};
  Object.entries(obj).forEach(([key, value]) => {
    const cleanKey = String(key)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    if (cleanKey) out[cleanKey] = value;
  });
  return out;
};

/**
 * Checks if a string is valid base64
 * @param {string} str - String to check
 * @returns {boolean} - True if valid base64
 */
export const isValidBase64 = (str) => {
  try {
    // Check if string contains only base64 characters
    const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
    if (!base64Regex.test(str)) {
      return false;
    }
    
    // Try to decode and re-encode to verify
    const decoded = atob(str);
    const reEncoded = btoa(decoded);
    return reEncoded === str;
  } catch (error) {
    return false;
  }
};

/**
 * Attempts to parse a string as JSON
 * @param {string} str - String to parse
 * @returns {object|null} - Parsed object or null if invalid JSON
 */
export const tryParseJSON = (str) => {
  try {
    return JSON.parse(str);
  } catch (error) {
    return null;
  }
};

/**
 * Detects if URL contains base64 encoded data or query parameters
 * @param {string} url - Current URL
 * @returns {object} - Detection result with format and data
 */
export const detectDataFormat = (url) => {
  const urlObj = new URL(url);
  const searchParams = urlObj.searchParams;

  const dataParam = searchParams.get('data');
  if (dataParam) {
    const parsedData = decodeVarsPayload(dataParam);
    if (parsedData && typeof parsedData === 'object') {
      return {
        format: 'base64',
        data: normalizeKeys(parsedData),
        source: 'data parameter'
      };
    }
    console.warn('Failed to decode data parameter payload');
  }

  // Backward compat: `config` parameter
  const configParam = searchParams.get('config');
  if (configParam) {
    const parsedConfig = decodeVarsPayload(configParam);
    if (parsedConfig && typeof parsedConfig === 'object') {
      return {
        format: 'base64',
        data: normalizeKeys(parsedConfig),
        source: 'config parameter'
      };
    }
    console.warn('Failed to decode config parameter payload');
  }
  
  // Check if the entire query string (minus common params) looks like base64
  const queryString = urlObj.search.substring(1);
  const commonParams = ['project', 'agent', 'id'];
  const filteredParams = queryString.split('&').filter(param => {
    const [key] = param.split('=');
    return !commonParams.includes(key.toLowerCase());
  }).join('&');
  
  if (filteredParams && isValidBase64(filteredParams)) {
    try {
      const decodedQuery = atob(filteredParams);
      const parsedQuery = tryParseJSON(decodedQuery);
      
      if (parsedQuery && typeof parsedQuery === 'object') {
        return {
          format: 'base64',
          data: parsedQuery,
          source: 'query string'
        };
      }
    } catch (error) {
      console.warn('Failed to decode base64 query:', error);
    }
  }
  
  // Default to query parameters
  const queryData = {};
  for (const [key, value] of searchParams.entries()) {
    // Convert param to a clean variable name
    const cleanParam = key.toLowerCase().replace(/[^a-z0-9]/g, '_');
    queryData[cleanParam] = value;
  }
  
  return {
    format: 'query',
    data: queryData,
    source: 'query parameters'
  };
};

/**
 * Merges dynamic variables with project defaults
 * @param {object} detectedData - Data from detection
 * @param {object} projectDefaults - Project default variables
 * @returns {object} - Merged variables
 */
export const mergeWithDefaults = (detectedData, projectDefaults) => {
  const merged = { ...detectedData };
  
  // Add project-specific defaults only if parameters don't exist
  Object.entries(projectDefaults).forEach(([key, varConfig]) => {
    const cleanParam = varConfig.param.toLowerCase().replace(/[^a-z0-9]/g, '_');
    if (!merged[cleanParam]) {
      merged[cleanParam] = varConfig.default;
    }
  });
  
  return merged;
};

/**
 * Main function to parse dynamic variables from URL
 * @param {string} url - Current URL
 * @param {object} projectDefaults - Project default variables
 * @returns {object} - Parsed variables with metadata
 */
export const parseDynamicVariables = (url, projectDefaults = {}) => {
  const detection = detectDataFormat(url);
  const mergedVars = mergeWithDefaults(detection.data, projectDefaults);
  
  return {
    variables: mergedVars,
    format: detection.format,
    source: detection.source,
    originalData: detection.data
  };
};
